import { useRef, useContext } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { IntroContext } from "@/App";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { useSeo } from "@/hooks/useSeo";
import { fileUrl } from "@/api";
import { HERO_IMAGE } from "@/data/projects";
import {
  Page,
  Reveal,
  MaskedLine,
  EASE,
} from "@/components/Page";
import { ProjectCard } from "@/components/ProjectCard";
import { Marquee } from "@/components/Marquee";
import { SolidorLogo } from "@/components/SolidorLogo";

const localized = (value, lang, fallback = "") => {
  if (value == null) return fallback;

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "object") {
    return value[lang] ?? value.tr ?? value.en ?? fallback;
  }

  return fallback;
};

const safeUpper = (text, lang) =>
  String(text || "").toLocaleUpperCase(
    lang === "tr" ? "tr-TR" : "en-US"
  );

const Hero = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const intro = useContext(IntroContext);
  const { lang, t } = useLang();

  const d = intro ? 3.2 : 0.3;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "18%"]
  );

  const opacity = useTransform(
    scrollYProgress,
    [0, 0.8],
    [1, 0]
  );

  return (
    <section
      ref={ref}
      data-testid="homepage-hero-section"
      className="relative h-[100svh] overflow-hidden bg-ink"
    >
      <motion.div
        className="absolute inset-0"
        style={reduced ? undefined : { y }}
      >
        <motion.img
          src={HERO_IMAGE}
          alt="SOLIDOR architectural project"
          className="h-[115%] w-full object-cover"
          initial={{
            scale: 1.12,
            opacity: 0.6,
          }}
          animate={{
            scale: 1,
            opacity: 1,
          }}
          transition={{
            duration: 2.2,
            delay: d - 0.2,
            ease: EASE,
          }}
        />

        <div className="absolute inset-0 bg-ink/35" />
      </motion.div>

      <motion.div
        className="absolute inset-0 flex flex-col justify-end px-5 sm:px-10 pb-32 sm:pb-24 text-white"
        style={reduced ? undefined : { opacity }}
      >
        <MaskedLine delay={d}>
          <SolidorLogo
            className="h-24 sm:h-36"
            color="#FFFFFF"
          />
        </MaskedLine>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.4em] text-white/80">
            <MaskedLine delay={d + 0.25}>
              {t.hero.tagline}
            </MaskedLine>
          </p>

          <p className="font-mono text-[11px] sm:text-xs tracking-[0.4em] text-white/60 pb-14 sm:pb-0">
            <MaskedLine delay={d + 0.4}>
              {t.hero.location}
            </MaskedLine>
          </p>
        </div>
      </motion.div>

      <motion.div
        data-testid="hero-scroll-indicator"
        className="absolute bottom-7 left-auto right-5 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 flex flex-col items-center gap-3 text-white/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: d + 1.2,
          duration: 1,
        }}
      >
        <span className="font-mono text-[9px] tracking-[0.4em]">
          {safeUpper(t.hero.scroll, lang)}
        </span>

        <div className="h-10 sm:h-12 w-px bg-white/30 overflow-hidden">
          <div className="scroll-line h-full w-px bg-white" />
        </div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  const { lang, t } = useLang();
  const cms = useContent();

  const seo = cms.site?.seo?.home || {};

  useSeo({
    title: localized(seo.title, lang, ""),
    description: localized(seo.description, lang, ""),
    image: cms.site?.seo?.og_image
      ? fileUrl(cms.site.seo.og_image)
      : undefined,
  });

  const items = Array.isArray(cms.projects)
    ? cms.projects
    : [];

  const rows = [];
  let i = 0;

  while (i < items.length) {
    if (i % 3 === 0) {
      rows.push([
        {
          p: items[i],
          i: i++,
        },
      ]);
    } else {
      const pair = [
        {
          p: items[i],
          i: i++,
        },
      ];

      if (i < items.length) {
        pair.push({
          p: items[i],
          i: i++,
        });
      }

      rows.push(pair);
    }
  }

  const rawManifesto =
    cms.site?.manifesto?.[lang] ||
    localized(cms.site?.manifesto, lang, null) ||
    t.manifesto.chapters;

  const manifesto = Array.isArray(rawManifesto)
    ? rawManifesto
    : t.manifesto.chapters;

  const cta = localized(
    cms.site?.cta,
    lang,
    t.cta.title
  );

  return (
    <Page testid="home-page">
      <Hero />

      <Marquee />

      <section
        id="projects"
        data-testid="selected-projects-section"
        className="bg-bone px-5 sm:px-10 pt-24 sm:pt-36 pb-28"
      >
        <Reveal className="flex items-end justify-between border-b border-ink/10 pb-6 mb-14 sm:mb-20">
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-none">
            {safeUpper(t.projects.title, lang)}
          </h2>

          <span className="hidden sm:block font-mono text-[10px] tracking-[0.3em] text-clay">
            {t.projects.range}
          </span>
        </Reveal>

        {rows.map((row, ri) =>
          row.length === 1 ? (
            <div
              key={ri}
              className={
                ri === 0
                  ? ""
                  : "mt-20 sm:mt-28"
              }
            >
              <ProjectCard
                project={row[0].p}
                index={row[0].i}
                wide
              />
            </div>
          ) : (
            <div
              key={ri}
              className="mt-20 sm:mt-28 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-10"
            >
              <ProjectCard
                project={row[0].p}
                index={row[0].i}
              />

              {row[1] && (
                <div className="md:mt-32">
                  <ProjectCard
                    project={row[1].p}
                    index={row[1].i}
                  />
                </div>
              )}
            </div>
          )
        )}
      </section>

      <section
        data-testid="manifesto-section"
        className="bg-solidor text-white px-5 sm:px-10 py-28 sm:py-40"
      >
        <Reveal>
          <span className="font-mono text-[10px] tracking-[0.35em] text-white/50">
            {safeUpper(t.manifesto.label, lang)}
          </span>
        </Reveal>

        <div className="mt-14 flex flex-col">
          {manifesto.map((c, i) => {
            const number =
              localized(c?.n, lang, "") ||
              String(i + 1).padStart(2, "0");

            const title = localized(
              c?.title,
              lang,
              ""
            );

            const text = localized(
              c?.text,
              lang,
              ""
            );

            return (
              <Reveal
                key={`${number}-${i}`}
                delay={i * 0.05}
              >
                <div
                  data-testid={`manifesto-chapter-${number}`}
                  className="grid grid-cols-12 gap-4 border-t border-white/15 py-12 sm:py-16"
                >
                  <span className="col-span-2 sm:col-span-1 font-mono text-xs text-white/50 pt-1">
                    {number}
                  </span>

                  <h3 className="col-span-10 sm:col-span-4 text-xl sm:text-2xl font-light tracking-tight">
                    {safeUpper(title, lang)}
                  </h3>

                  <p className="col-span-10 col-start-3 sm:col-span-6 sm:col-start-7 text-sm sm:text-base font-light leading-relaxed text-white/70">
                    {text}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section
        data-testid="home-contact-cta"
        className="bg-stone px-5 sm:px-10 py-28 sm:py-36"
      >
        <Reveal className="flex flex-col items-start gap-10">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.05] max-w-3xl">
            {safeUpper(cta, lang)}
          </h2>

          <Link
            to="/contact"
            data-testid="home-cta-contact-link"
            className="group inline-flex items-center gap-4 font-mono text-[11px] tracking-[0.3em]"
          >
            <span className="border-b border-ink pb-1 transition-opacity duration-300 group-hover:opacity-50">
              {safeUpper(t.cta.link, lang)}
            </span>

            <span
              aria-hidden
              className="transition-transform duration-500 group-hover:translate-x-2"
            >
              →
            </span>
          </Link>
        </Reveal>
      </section>
    </Page>
  );
}
